/**
 * setLibrary — the data-driven catalogue of named swim-set patterns.
 *
 * STRICT, NEVER-GUESS contract (owner decision, SETLABEL-01): a set is labeled
 * ONLY when its ordered stroke sequence (and, for kick/drill, its timing) is a
 * confident match for exactly one pattern here. The matcher (setMatch.ts) walks
 * this array most-specific → general and returns the FIRST match's name, or null.
 * A null result leaves the set's existing title untouched ("Mixed set"). A false
 * positive is worse than no label — proven on the June-8 free-framed palindrome,
 * which must NOT read as "The Rockalita".
 *
 * UNKNOWN → BUTTERFLY WILDCARD: the Apple Watch frequently mislabels a Butterfly
 * length as "Unknown". So wherever a pattern slot EXPECTS Butterfly, an "Unknown"
 * actual length satisfies it. "Unknown" wildcards NOTHING else. This rule lives in
 * exactly one place — `slotMatches` — and every predicate routes Butterfly slots
 * through it.
 *
 * ADDING A SET TYPE: append ONE entry to SET_LIBRARY (ordered specific→general; a
 * more-specific pattern must sit ABOVE a more-general one that could also match).
 * No matcher refactor — matchSet interprets `kind` + `params`. See EXTENSION POINT.
 *
 * Pure data module (no Svelte runtime), per lib convention.
 */
import type { Lap } from "./laps";

/** Stroke vocabulary (HealthKit decoded). "Unknown" is the watch's fly-mislabel. */
export const FREE = "Freestyle";
export const BACK = "Backstroke";
export const BREAST = "Breaststroke";
export const FLY = "Butterfly";
export const UNKNOWN = "Unknown";

/** The four-stroke IM order and its reverse (used by the IM / Reverse-IM kinds). */
export const IM_ORDER: readonly string[] = [FLY, BACK, BREAST, FREE];
export const REVERSE_IM_ORDER: readonly string[] = [FREE, BREAST, BACK, FLY];

/** The strokes given, IM strokes first in IM order, then any others as first seen. */
export function inImOrder(strokes: Iterable<string>): string[] {
  const all = [...new Set(strokes)];
  return [...IM_ORDER.filter((s) => all.includes(s)), ...all.filter((s) => !IM_ORDER.includes(s))];
}

/** Broken IM — each IM stroke followed by a freestyle, in IM order. The 4th IM
 *  stroke IS freestyle, so the 8-length cycle ends free-free. Reverse is the
 *  exact mirror. Both repeat for longer sets (16 = double). Fly slots wildcard. */
export const BROKEN_IM_TEMPLATE: readonly string[] = [FLY, FREE, BACK, FREE, BREAST, FREE, FREE, FREE];
export const REVERSE_BROKEN_IM_TEMPLATE: readonly string[] = [FREE, FREE, FREE, BREAST, FREE, BACK, FREE, FLY];

// Display names for the exact-sequence signature sets. Each is used in BOTH the
// SET_LIBRARY entry's label and SET_DISPLAY, so a rename is one edit here.
const KING_SANDWICH_NAME = "King Sized Butterfly Sandwich";
const CRYPTIC_IM_NAME = "Cryptic IM";
const MOSESSO_NAME = "Mosesso";
const REVERSE_MOSESSO_NAME = "Reverse Mosesso";
const MOSALITA_NAME = "Mosalita";

// King Sized Butterfly Sandwich — 200m: 50 fly / 25 breast / 50 free / 25 breast / 50 fly.
export const KING_BUTTERFLY_SANDWICH_TEMPLATE: readonly string[] =
  [FLY, FLY, BREAST, FREE, FREE, BREAST, FLY, FLY];

// Cryptic IM — "IM down, reverse IM back" in a deliberately SCRAMBLED order
// (fly, free, back, breast / mirrored). The true IM order down-and-back is
// already a Rockalita; the scramble is what makes this its own set.
export const CRYPTIC_IM_TEMPLATE: readonly string[] =
  [FLY, FREE, BACK, BREAST, BREAST, BACK, FREE, FLY];

// Mosesso — 500m ascending pyramid; each round adds the next stroke:
// (25 fly), (25 fly, 50 back), (+75 breast), (+100 free).
export const MOSESSO_TEMPLATE: readonly string[] = [
  FLY,
  FLY, BACK, BACK,
  FLY, BACK, BACK, BREAST, BREAST, BREAST,
  FLY, BACK, BACK, BREAST, BREAST, BREAST, FREE, FREE, FREE, FREE,
];

// Reverse Mosesso — the same 500 swum back down the pyramid; derived so the
// mirror invariant can't drift. Mosalita — a Mosesso straight into its reverse.
export const REVERSE_MOSESSO_TEMPLATE: readonly string[] = [...MOSESSO_TEMPLATE].reverse();
export const MOSALITA_TEMPLATE: readonly string[] = [...MOSESSO_TEMPLATE, ...REVERSE_MOSESSO_TEMPLATE];

/**
 * slotMatches — the single home of the Unknown→Butterfly wildcard rule.
 *
 * Returns true when an actual length's stroke satisfies a pattern slot's expected
 * stroke. Exact equality, EXCEPT: when the slot expects Butterfly, an "Unknown"
 * actual length also satisfies it (watch fly-mislabel). "Unknown" never satisfies
 * any other expected stroke.
 *
 *   slotMatches("Freestyle",  "Freestyle") === true
 *   slotMatches("Butterfly",  "Unknown")   === true   // wildcard
 *   slotMatches("Backstroke", "Unknown")   === false  // Unknown only wildcards Fly
 */
export function slotMatches(expected: string, actual: string): boolean {
  if (expected === actual) return true;
  if (expected === FLY && actual === UNKNOWN) return true;
  return false;
}

/** A length counts as a "B-stroke" (non-Free stroke) for Killer BS membership.
 *  Unknown counts as Fly. Freestyle is explicitly NOT a B-stroke. */
export function isBStroke(stroke: string): boolean {
  return stroke === BACK || stroke === BREAST || stroke === FLY || stroke === UNKNOWN;
}

/** Normalize Unknown→Butterfly so distinct-stroke counting treats a wildcarded
 *  fly length as the Fly it almost certainly is. Used by Killer BS only. */
export function canonicalBStroke(stroke: string): string {
  return stroke === UNKNOWN ? FLY : stroke;
}

/** The kinds matchSet knows how to interpret. Each maps to a small predicate. */
export type SetKind =
  | "rockalita"
  | "butterflySandwich"
  | "reps125"
  | "bookEnded"
  | "imGroups"
  | "templateGroups"
  | "exactSequence"
  | "killerBs"
  | "kickDrill"
  | "strokeFreeAlternating"
  | "singleStrokeBlock";

export interface SetDefinition {
  /** Stable internal id; not user-facing. */
  id: string;
  /** Predicate family the matcher runs. */
  kind: SetKind;
  /** Kind-specific parameters (e.g. the ordered IM template). */
  params?: Record<string, unknown>;
}

/**
 * SET_LIBRARY — ordered most-specific → general. First confident match wins.
 *
 * Order rationale:
 *   1 Rockalita      — an exact 8-length palindrome; the most constrained shape.
 *   2 IM             — groups-of-4 in IM order.
 *   3 Reverse IM     — groups-of-4 in reverse-IM order.
 *   4 Killer BS      — a multi-B-stroke block with no Free; broader than IM.
 *   5 Kick / drill   — timing-driven; predominantly "no-arms" lengths.
 *   6 Stroke/Free    — strict Free/X alternation.
 *   7 Single-stroke  — the most general shape (all one stroke); must sit LAST so a
 *                      more-specific pattern is never pre-empted.
 */
export const SET_LIBRARY: readonly SetDefinition[] = [
  // 1. The Rockalita — exactly 8 lengths, [Fl, Bk, Br, M, M, Br, Bk, Fl] with both
  //    middle lengths the SAME single stroke (palindrome on an IM spine). Fl slots
  //    accept the Unknown wildcard.
  { id: "rockalita", kind: "rockalita" },

  // 1b. Butterfly Sandwich — exactly 8 lengths, [Fl, Br, A, B, B, A, Br, Fl]: Fly
  //     "bread" + Breast "spread" bookends wrapping a 4-length PALINDROME filling
  //     (pos2==pos5, pos3==pos4) that may be any strokes. Fly slots wildcard.
  //     Mutually exclusive with Rockalita (pos1 is Breast here, Backstroke there).
  { id: "butterflySandwich", kind: "butterflySandwich" },

  // 1c. Exact-sequence signature sets — fixed-length, slot-for-slot templates
  //     (Fly slots accept the Unknown wildcard). Mutually exclusive with each
  //     other (distinct lengths; the two 8s conflict at position 1) and with
  //     Rockalita / Butterfly Sandwich (position-1 conflicts). This block MUST
  //     stay above bookEnded: the King and the Mosalita both open and close
  //     [Fl,Fl]…[Fl,Fl], so they pass bookEnded's end-check and survive today
  //     only because its recursive middle-match happens to fail.
  { id: "kingButterflySandwich", kind: "exactSequence", params: { template: KING_BUTTERFLY_SANDWICH_TEMPLATE, label: KING_SANDWICH_NAME } },
  { id: "crypticIm", kind: "exactSequence", params: { template: CRYPTIC_IM_TEMPLATE, label: CRYPTIC_IM_NAME } },
  { id: "mosalita", kind: "exactSequence", params: { template: MOSALITA_TEMPLATE, label: MOSALITA_NAME } },
  { id: "mosesso", kind: "exactSequence", params: { template: MOSESSO_TEMPLATE, label: MOSESSO_NAME } },
  { id: "reverseMosesso", kind: "exactSequence", params: { template: REVERSE_MOSESSO_TEMPLATE, label: REVERSE_MOSESSO_NAME } },

  // 2. 4×125 — groups of 5: four Freestyle + one "unique 25" cycling IM order
  //    (Fly, Back, Breast, Free) across reps. >=2 reps (>=10 lengths). Labeled
  //    "{reps}×125" (assumes 25m pool). Fly slot accepts the Unknown wildcard.
  //    Best-effort: if the watch splits the reps into separate sets this won't
  //    fire — validate against a real swim and revisit.
  { id: "reps125", kind: "reps125" },

  // 3. Book-ended {set} — 2 lengths of one stroke (often free) on EACH end, same
  //    stroke both ends, wrapping a core set matched RECURSIVELY → "Book-ended
  //    Rockalita", etc. High in the order so a structured book-end is caught before
  //    general patterns; cannot pre-empt a plain 8-length Rockalita (needs matching
  //    2-length single-stroke ends). Guard rejects all-same-stroke middles.
  { id: "bookEnded", kind: "bookEnded" },

  // 3. IM — length divisible by 4 (>=4); each group of 4 is [Fl, Bk, Br, Fr] in
  //    order. Fl slot accepts the Unknown wildcard.
  { id: "im", kind: "imGroups", params: { order: IM_ORDER, label: "IM" } },

  // 3. Reverse IM — groups of 4 = [Fr, Br, Bk, Fl] in order. Fl accepts wildcard.
  { id: "reverseIm", kind: "imGroups", params: { order: REVERSE_IM_ORDER, label: "Reverse IM" } },

  // 3a. Broken IM — each IM stroke followed by a free, in IM order (8-length cycle,
  //     ends free-free). Repeats for longer. Fly slots accept the Unknown wildcard.
  { id: "brokenIm", kind: "templateGroups", params: { template: BROKEN_IM_TEMPLATE, label: "Broken IM" } },

  // 3b. Reverse Broken IM — the exact mirror of Broken IM.
  { id: "reverseBrokenIm", kind: "templateGroups", params: { template: REVERSE_BROKEN_IM_TEMPLATE, label: "Reverse Broken IM" } },

  // 4. Killer BS — every length a B-stroke (Br/Bk/Fl, Unknown counts as Fl), NO
  //    Freestyle anywhere; >=2 DISTINCT B-strokes each appearing in >=2 lengths;
  //    AND no single stroke exceeds 75% of the set. (Rejects a single-stroke block
  //    with one stray — that fails both the distinct-count and the 75% cap.)
  { id: "killerBs", kind: "killerBs" },

  // 5. Kick / drill — predominantly "no-arms" lengths: MEASURED swolf whose implied
  //    strokes (swolf - durationSeconds) <= 3, AND durationSeconds >= 15 (phantom
  //    short-lap guard). Need >=2 such lengths forming the MAJORITY of the set.
  //    A length with NO swolf is unknown, never "no arms" (roadmap 34) — it stays
  //    in the denominator, so it can only prevent a match, never create one.
  { id: "kickDrill", kind: "kickDrill" },

  // 6. Stroke / Free alternating — strict Free/X alternation with ONE fixed
  //    non-free stroke X; minimum 4 lengths (2 full pairs). Label "{X} / Free".
  { id: "strokeFreeAlternating", kind: "strokeFreeAlternating" },

  // 7. Single-stroke block — all lengths the same stroke (>=2). All Free →
  //    "Straight Freestyle"; otherwise the stroke name.
  { id: "singleStrokeBlock", kind: "singleStrokeBlock" },

  // ── EXTENSION POINT — append a new SetDefinition here, then handle its `kind`
  //    in setMatch.ts. Keep most-specific patterns ABOVE more-general ones. The
  //    generic "templateGroups" kind already covers any fixed repeating stroke
  //    sequence (see Broken IM / Reverse Broken IM above) — many new named sets
  //    need only a template constant + one SET_LIBRARY entry, no matcher change.
];

/** Re-export the Lap type alias the matcher consumes (single import surface). */
export type { Lap };

// ─── Display map ────────────────────────────────────────────────────────────

export interface SetDisplay {
  displayName: string;
  /** Jargon-free, non-swimmer-readable. NO health causation claims. */
  plainDefinition: string;
  isSignature: boolean;
}

// health copy: association only, no causation claims
export const SET_DISPLAY: Record<string, SetDisplay> = {
  // --- 17 signature sets ---
  rockalita:             { displayName: "The Rockalita",        plainDefinition: "The strokes you swim out are mirrored coming back, over 200m.", isSignature: true },
  butterflySandwich:     { displayName: "Butterfly Sandwich",   plainDefinition: "Butterfly on both ends with other strokes filling the middle.",          isSignature: true },
  kingButterflySandwich: { displayName: KING_SANDWICH_NAME,     plainDefinition: "A double-decker sandwich: two butterfly lengths on each end around breaststroke and front crawl.", isSignature: true },
  crypticIm:             { displayName: CRYPTIC_IM_NAME,        plainDefinition: "All four strokes out and back again, in a scrambled order.",             isSignature: true },
  mosesso:               { displayName: MOSESSO_NAME,           plainDefinition: "A growing pyramid: each round adds one more stroke than the last.",      isSignature: true },
  reverseMosesso:        { displayName: REVERSE_MOSESSO_NAME,   plainDefinition: "A shrinking pyramid: each round drops one stroke from the last.",        isSignature: true },
  mosalita:              { displayName: MOSALITA_NAME,          plainDefinition: "The full kilometer: a growing pyramid followed by its mirror back down.", isSignature: true },
  reps125:               { displayName: "125s Set",             plainDefinition: "Repeats of 125, each with one rotating odd length.",                     isSignature: true },
  bookEnded:             { displayName: "Book-ended Set",       plainDefinition: "The same stroke wraps the start and finish around a different middle.",   isSignature: true },
  im:                    { displayName: "IM",                   plainDefinition: "All four strokes in order: fly, back, breast, free.",                    isSignature: true },
  reverseIm:             { displayName: "Reverse IM",           plainDefinition: "All four strokes in reverse: free, breast, back, fly.",                  isSignature: true },
  brokenIm:              { displayName: "Broken IM",            plainDefinition: "The four strokes of an IM, split up with rest between.",                 isSignature: true },
  reverseBrokenIm:       { displayName: "Reverse Broken IM",    plainDefinition: "A broken IM swum in reverse stroke order.",                              isSignature: true },
  killerBs:              { displayName: "Killer BS",            plainDefinition: "Only the hard strokes: back, breast, and butterfly. No freestyle.",     isSignature: true },
  kickDrill:             { displayName: "Kick / Drill",         plainDefinition: "Lengths with no arm strokes counted: kicking or drilling.",             isSignature: true },
  strokeFreeAlternating: { displayName: "Stroke / Free",        plainDefinition: "Every other length is front crawl; the lengths between are one other stroke.", isSignature: true },
  singleStrokeBlock:     { displayName: "Single-Stroke Block",  plainDefinition: "A run of lengths that all use the same stroke.",                          isSignature: true },
  // --- 4 everyday sets ---
  warmup:                { displayName: "Warmup",               plainDefinition: "The easy lengths you start with.",                                       isSignature: false },
  cooldown:              { displayName: "Cooldown",             plainDefinition: "The easy lengths you finish with.",                                      isSignature: false },
  freeRepeats:           { displayName: "Freestyle set",        plainDefinition: "Several lengths in a row, all front crawl.",                              isSignature: false },
  straightStroke:        { displayName: "Stroke Set",           plainDefinition: "Several lengths in a row, all the same stroke: back, breast, or butterfly.", isSignature: false },
};

export const LEDGER_SET_IDS: readonly string[] = Object.keys(SET_DISPLAY);
